import * as React from "react";
import cx from "classnames";

import { DatasheetItem } from '@modules/datasheets/components/datasheet-item';
import { MovableItem } from "../movable-item";
import { TextElementProps, useTextElementController } from "./text-element.controller";

import { TextArea } from "@common/components/text-area";
import { DatasheetComponentTextStyle } from "@common/dto/datasheet/component-settings/text-style";
import { DatasheetFieldComponentTextAlign } from "@common/dto/datasheet/field-component/text-align";
import { normalizeFontFamilyName } from "@modules/datasheets/utils/normalize-font-family-name";

import "./text-element.styles.scss";

export const TextElement: React.FC<TextElementProps> = React.memo((props) => {
  const { datasheetElement, multipleElementsSelected, isTabOrderSettingActive } = props;

  const textStyles = React.useMemo(() => {
    const textStyleValue = datasheetElement.original.textstyle;
    
    if (Array.isArray(textStyleValue)) {
      return textStyleValue.filter((s): s is DatasheetComponentTextStyle => 
        s === DatasheetComponentTextStyle.bold || 
        s === DatasheetComponentTextStyle.italic || 
        s === DatasheetComponentTextStyle.underline
      );
    }
    
    // If it's a number (bitmask), decode it using bitwise AND checks
    if (typeof textStyleValue === 'number' && textStyleValue !== 0 && textStyleValue !== 33) {
      const styles: Array<DatasheetComponentTextStyle> = [];
      
      // Use bitwise AND to check which flags are set
      if ((textStyleValue & DatasheetComponentTextStyle.bold) === DatasheetComponentTextStyle.bold) {
        styles.push(DatasheetComponentTextStyle.bold);
      }
      if ((textStyleValue & DatasheetComponentTextStyle.italic) === DatasheetComponentTextStyle.italic) {
        styles.push(DatasheetComponentTextStyle.italic);
      }
      if ((textStyleValue & DatasheetComponentTextStyle.underline) === DatasheetComponentTextStyle.underline) {
        styles.push(DatasheetComponentTextStyle.underline);
      }
      
      return styles;
    }
    
    return [];
  }, [datasheetElement.original.textstyle]);

  const {
    coordinates,
    datasheetElementSelected,
    handleDatasheetElementMouseDown,
    movableElementRef,
    handleResizing,
    size,
    handleTextChange
  } = useTextElementController(props);

  // Convert fontColor number (BGR format) to hex color string (RGB format), or use hexColor if available
  const getColor = React.useMemo(() => {
    if (datasheetElement.original.hexColor) {
      return datasheetElement.original.hexColor;
    }
    if (datasheetElement.original.fontColor !== undefined && datasheetElement.original.fontColor !== null) {
      // Extract BGR bytes from number and output as RGB hex
      // Number is stored as BGR: bits 0-7 = Blue, 8-15 = Green, 16-23 = Red
      const blue = (datasheetElement.original.fontColor & 0xFF);
      const green = ((datasheetElement.original.fontColor >> 8) & 0xFF);
      const red = ((datasheetElement.original.fontColor >> 16) & 0xFF);
      // Output as RGB hex string
      const hex = [
        red.toString(16).padStart(2, '0'),
        green.toString(16).padStart(2, '0'),
        blue.toString(16).padStart(2, '0')
      ].join('');
      return `#${hex}`;
    }
    return '#000000';
  }, [datasheetElement.original.fontColor, datasheetElement.original.hexColor]);

  // Get text alignment, defaulting to left if not set
  const getTextAlign = React.useMemo(() => {
    const align = datasheetElement.original.align ?? DatasheetFieldComponentTextAlign.left;
    switch (align) {
      case DatasheetFieldComponentTextAlign.center:
        return 'center';
      case DatasheetFieldComponentTextAlign.right:
        return 'right';
      case DatasheetFieldComponentTextAlign.left:
      default:
        return 'left';
    }
  }, [datasheetElement.original.align]);

  return (
    <DatasheetItem
      style={{
        top: coordinates.y,
        left: coordinates.x,
        display: "block",
      }}
    >
      <MovableItem
        resizing={{
          enabled: isTabOrderSettingActive ? false : !multipleElementsSelected,
          onChange: handleResizing,
        }}
        moving={{
          enabled: !isTabOrderSettingActive,
        }}
        reference={movableElementRef}
        height={size.height}
        width={size.width}
        selected={datasheetElementSelected}
        onMouseDown={isTabOrderSettingActive ? undefined : handleDatasheetElementMouseDown}
        className={cx("DatasheetsEditorDatasheetTextElement__movable-item", {
          ["DatasheetsEditorDatasheetTextElement__movable-item--empty"]: !datasheetElement.original.text.trim(),
          ["DatasheetsEditorDatasheetTextElement__movable-item--active"]: datasheetElementSelected,
        })}
      >
        <TextArea
          className={cx(
            "DatasheetsEditorDatasheetTextElement__textarea",
            { "DatasheetsEditorDatasheetTextElement__textarea--disabled": isTabOrderSettingActive }
          )}
          style={{
            width: size.width,
            minHeight: size.height,
            fontSize: datasheetElement.original.fontsize,
            fontFamily: normalizeFontFamilyName(datasheetElement.original.fontname),
            color: getColor,
            fontWeight: textStyles.includes(DatasheetComponentTextStyle.bold)
              ? 700
              : undefined,
            fontStyle: textStyles.includes(DatasheetComponentTextStyle.italic)
              ? "italic"
              : undefined,
            textDecoration: textStyles.includes(DatasheetComponentTextStyle.underline)
              ? "underline"
              : undefined,
            textAlign: getTextAlign,
            lineHeight: '1.2',
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
          }}
          value={datasheetElement.original.text}
          onChange={handleTextChange}
        />
      </MovableItem>
    </DatasheetItem>
  );
});
